import { v, useSelector, createAction, assertPartialSchema, setSessionStorage } from "../../../lib";
import { endSessionScope, releaseSharedSessionNonce } from "../../../lib/auth/session_scope";
import { clearSharedSessionDeadlines } from "../../../lib/auth/session_deadlines";

// SELECTOR
export const useSelectAuth = () => {
  return useSelector((state) => state?.auth || null);
};
export const useSelectAccessToken = () => {
  return useSelector((state) => state?.auth?.accessToken || null);
};
// Whether private pages, the navbar's signed-in content and the overlays above the routes may
// render. Past an idle end the server has not yet confirmed, the token is kept but nothing private
// shows until the session is verified again or ends.
export const privateUiAllowed = (auth) => Boolean(auth?.accessToken) && !auth?.sessionUnverified;
export const useSelectPrivateUiAllowed = () => {
  return useSelector((state) => privateUiAllowed(state?.auth));
};
// ACTIONS
export const AUTH_SET = "AUTH_SET";
export const doAUTH_SET = createAction(AUTH_SET);

export const AUTH_REFRESHING = "AUTH_REFRESHING";
export const doAUTH_REFRESHING = createAction(AUTH_REFRESHING);

export const AUTH_REFRESHING_FAILURE = "AUTH_REFRESHING_FAILURE";
export const doAUTH_REFRESHING_FAILURE = createAction(AUTH_REFRESHING_FAILURE);

// a refresh failed for a reason that is not the end of the session (network, 5xx, timeout);
// the token is kept and a retry is scheduled for `nextRetryAt`
export const AUTH_REFRESH_UNAVAILABLE = "AUTH_REFRESH_UNAVAILABLE";
export const doAUTH_REFRESH_UNAVAILABLE = createAction(AUTH_REFRESH_UNAVAILABLE);

// a signal for the retry scheduler to stop waiting; it carries no state of its own
export const AUTH_REFRESH_RETRY_NOW = "AUTH_REFRESH_RETRY_NOW";
export const doAUTH_REFRESH_RETRY_NOW = createAction(AUTH_REFRESH_RETRY_NOW);

// what the server said about when the session ends, from a reply that carries no new token (an
// activity report); the token and any scheduled refresh retry are left exactly as they are
export const AUTH_SESSION_DEADLINES_SET = "AUTH_SESSION_DEADLINES_SET";
export const doAUTH_SESSION_DEADLINES_SET = createAction(AUTH_SESSION_DEADLINES_SET);

// set by the idle tracker when an idle end falls due without a fresh reply from the server, and
// cleared by it only when it accepts a fresh answer that keeps the session going
export const AUTH_SESSION_UNVERIFIED_SET = "AUTH_SESSION_UNVERIFIED_SET";
export const doAUTH_SESSION_UNVERIFIED_SET = createAction(AUTH_SESSION_UNVERIFIED_SET);

export const AUTH_SESSION_UNVERIFIED_CLEAR = "AUTH_SESSION_UNVERIFIED_CLEAR";
export const doAUTH_SESSION_UNVERIFIED_CLEAR = createAction(AUTH_SESSION_UNVERIFIED_CLEAR);

export const LOGOUT = "LOGOUT";
export const doLOGOUT = createAction(LOGOUT);

export const authActionCreators = {
  doAUTH_SET,
  doAUTH_REFRESHING,
  doAUTH_REFRESHING_FAILURE,
  doAUTH_REFRESH_UNAVAILABLE,
  doAUTH_REFRESH_RETRY_NOW,
  doAUTH_SESSION_DEADLINES_SET,
  doAUTH_SESSION_UNVERIFIED_SET,
  doAUTH_SESSION_UNVERIFIED_CLEAR,
  doLOGOUT,
};

// REDUCER
export const authDefaultState = {
  accessToken: null,
  isRefreshing: false,
  accessTokenExpireTimestamp: 0,
  refreshTokenExpireTimestamp: 0,
  // `isRefreshing` means "a refresh request is in flight"; the three fields below mean
  // "refreshing is failing transiently and a retry is scheduled", which outlives one attempt
  refreshUnavailable: false,
  refreshRetryAttempt: 0,
  refreshNextRetryAt: null,
  // the browser sign-in this token belongs to (`session_scope.ts`) and the server's deadlines for it
  // (`session_deadlines.ts`); a token persisted without a nonce is not trusted at boot
  sessionNonce: null,
  sessionDeadlines: null,
  // past an idle end and not yet confirmed by the server (`privateUiAllowed`). Never persisted:
  // a reload starts without it and the tracker decides again
  sessionUnverified: false,
};

const sessionDeadlinesSchema = v.object({
  nonce: v.string().exist(),
  capEndsAt: v.number().allow(null).exist(),
  idleEndsAt: v.number().allow(null).exist(),
  sampleSeq: v.number().exist(),
  observedAt: v.number().exist(),
  observedAtMono: v.number().allow(null).exist(),
  revalidateAfterSeq: v.number().allow(null).exist(),
  // the idle policy; a reading without it reads as not enforced. Strict, or Joi would pass "true" through
  idleEnforced: v.boolean().strict().optional(),
  policyObservedAt: v.number().allow(null).optional(),
  fallbackSince: v.number().allow(null).optional(),
});

// The session is over in this tab. Private work captured under its scope must see that before
// anything else runs, and the other tabs of the same sign-in hear it through the shared records.
// A tab ended because another identity took over the browser holds an older nonce, so the newer
// sign-in's records are left alone.
const endSession = (sessionNonce) => {
  const nonce = endSessionScope()?.nonce ?? sessionNonce;
  if (!nonce) return;
  releaseSharedSessionNonce(nonce);
  clearSharedSessionDeadlines(nonce);
};

const transientRefreshDefaultState = {
  refreshUnavailable: false,
  refreshRetryAttempt: 0,
  refreshNextRetryAt: null,
};

const persistAuth = (auth) => {
  const persisted = { ...auth };
  delete persisted.sessionUnverified;
  setSessionStorage({ auth: persisted });
};

export const authReducer = (state = { ...authDefaultState }, { type, payload }) => {
  switch (type) {
    case AUTH_SET: {
      assertPartialSchema({
        payload,
        schema: v.object({
          accessToken: v.string().exist(),
          accessTokenExpiresInSeconds: v.number().exist(),
          refreshTokenExpiresInSeconds: v.number().exist(),
          sessionNonce: v.string().optional(),
          sessionDeadlines: sessionDeadlinesSchema.allow(null).optional(),
        }),
      });

      const { accessToken, accessTokenExpiresInSeconds, refreshTokenExpiresInSeconds } = payload;
      const now = new Date().getTime();
      const sessionNonce = payload.sessionNonce ?? null;

      const auth = {
        accessToken,
        accessTokenExpireTimestamp: now + accessTokenExpiresInSeconds * 1000,
        refreshTokenExpireTimestamp: now + refreshTokenExpiresInSeconds * 1000,
        isRefreshing: false,
        ...transientRefreshDefaultState,
        sessionNonce,
        sessionDeadlines: payload.sessionDeadlines ?? null,
        // a refresh's token is not the server confirming the session (the tracker clears that);
        // a new sign-in is a new session
        sessionUnverified: sessionNonce === state.sessionNonce && Boolean(state.sessionUnverified),
      };

      persistAuth(auth);
      return auth;
    }

    case AUTH_REFRESHING:
      // deliberately leaves the transient fields alone so a reconnecting notice stays mounted
      // while the scheduled attempt is in flight
      if (!state.isRefreshing) return { ...state, isRefreshing: true };
      else return state;

    case AUTH_REFRESH_UNAVAILABLE: {
      assertPartialSchema({
        payload,
        schema: v.object({
          attempt: v.number().integer().min(1).exist(),
          nextRetryAt: v.number().exist(),
        }),
      });

      const { attempt, nextRetryAt } = payload;
      return {
        ...state,
        isRefreshing: false,
        refreshUnavailable: true,
        refreshRetryAttempt: attempt,
        refreshNextRetryAt: nextRetryAt,
      };
    }

    case AUTH_SESSION_DEADLINES_SET: {
      assertPartialSchema({ payload, schema: sessionDeadlinesSchema });

      // a reply that outlived its sign-in says nothing about the current one
      if (!state.sessionNonce || payload.nonce !== state.sessionNonce) return state;

      const auth = { ...state, sessionDeadlines: payload };
      persistAuth(auth);
      return auth;
    }

    case AUTH_SESSION_UNVERIFIED_SET:
      return state.sessionUnverified ? state : { ...state, sessionUnverified: true };

    case AUTH_SESSION_UNVERIFIED_CLEAR:
      return state.sessionUnverified ? { ...state, sessionUnverified: false } : state;

    case AUTH_REFRESHING_FAILURE:
      // terminal: drop the persisted auth entry too, so a reload on the login page cannot
      // restore a token the backend has already retired
      endSession(state.sessionNonce);
      setSessionStorage({ auth: null });
      return {
        ...state,
        accessToken: null,
        isRefreshing: false,
        ...transientRefreshDefaultState,
        sessionNonce: null,
        sessionDeadlines: null,
        sessionUnverified: false,
      };

    case LOGOUT:
      endSession(state.sessionNonce);
      return authDefaultState;
    default:
      return state;
  }
};
